import { useGetPermanentEnsemblesRelatedToArtwork } from "@/api/artwork/artwork";
import { useUploadDocument } from "@/api/file";
import {
  administrativeDocumentTypeSchema,
  useGenerateAdministrativeDocumentsPdf,
} from "@/api/pdf";
import * as AF from "@/components/form-items/AbstractForm";
import PrintPreview from "@/components/print/PrintPreview";
import SelectedRecord from "@/components/print/SelectedRecord";
import { Button } from "@/components/ui/button";
import { CancelButton } from "@/components/ui/custom-buttons";
import { CustomDialogFooter } from "@/components/ui/custom-dialog";
import {
  CustomSheetBody,
  CustomSheetContent,
  CustomSheetHeader,
} from "@/components/ui/custom-sheet";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  HeadlessFormItem,
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetClose,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useAuth } from "@/context/auth/AuthContext";
import i18n, { SupportedLocale } from "@/i18n";
import { getDefaultImage } from "@/lib/image";
import { Artwork, Attachment } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = z.object({
  documentType: administrativeDocumentTypeSchema,

  inclusionLetter: z.boolean().default(false),
  informationLetter: z.boolean().default(false),
  hasBeenExamined: z.boolean().default(false),

  signingInfo: z.string().min(1),
});

export const AddAdministrativeDocumentsDrawer = ({
  artworkAttachments,
  artwork,
}: {
  artwork: Artwork;
  artworkAttachments: Attachment[];
}) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  const { t } = useTranslation();

  const { data: permanentEnsembles } = useGetPermanentEnsemblesRelatedToArtwork(
    artwork.id,
  );

  const queryClient = useQueryClient();

  const selectedPermanentEnsemble = useMemo(
    () => permanentEnsembles?.at(0),
    [permanentEnsembles],
  );

  const pdfGenerator = useGenerateAdministrativeDocumentsPdf();

  const fileSaver = useUploadDocument();

  const { instanceData } = useAuth();

  const createdFile = useRef<File>();
  const previewURL = createdFile.current
    ? URL.createObjectURL(createdFile.current)
    : undefined;

  const [previewOpen, setPreviewOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const image = useMemo(
    () => getDefaultImage(artworkAttachments, "artwork", "publicUrl"),
    [artworkAttachments],
  );

  function onSubmit({
    hasBeenExamined,
    signingInfo,
    documentType,
  }: z.infer<typeof formSchema>) {
    pdfGenerator.mutate(
      {
        artworkId: artwork.id,
        params: {
          documentType,
          oeuvreExaminee: hasBeenExamined,
          signloca: signingInfo,
        },
        lang: i18n.language as SupportedLocale,
      },
      {
        onSuccess: (file) => {
          createdFile.current = file;
          setPreviewOpen(true);
        },
      },
    );
  }

  function onSaveFile() {
    if (!createdFile.current || !instanceData) return;

    const fileName = `${artwork.oeuvresDescriptionNumeroEnregistrement}_Document Administratif`;

    fileSaver.mutate(
      {
        file: createdFile.current,
        data: {
          docId: artwork.id,
          mimetype: createdFile.current?.type ?? "application/pdf",
          sourceName: `${fileName}.pdf`,
          title: `${fileName}.pdf`,
          tag: "adminRecord",
          description: "",
        },
        s3bucket: instanceData.s3Bucket,
        instanceId: instanceData.id,
      },
      {
        onSuccess: () => {
          try {
            // Download the PDF file
            const url = URL.createObjectURL(createdFile.current!);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${fileName}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          } catch (error) {
            console.error(error);
          }

          setPreviewOpen(false);
          setSheetOpen(false);
          queryClient.invalidateQueries({
            queryKey: ["artworks", artwork.id, "administrative-documents"],
          });
          form.reset();
        },
      },
    );
  }

  return (
    <>
      <PrintPreview
        confirmButton={
          <Button onClick={onSaveFile} disabled={fileSaver.isPending}>
            {fileSaver.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : null}
            {t("saveDocument")}
          </Button>
        }
        fileURL={previewURL as string}
        onOpenChange={setPreviewOpen}
        open={previewOpen}
      />
      <Sheet onOpenChange={setSheetOpen} open={sheetOpen}>
        <SheetTrigger asChild>
          <Button variant="outline">
            {t("oeuvresCreateNewAdministrativeDocument")}
          </Button>
        </SheetTrigger>
        <CustomSheetContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col h-full"
            >
              <CustomSheetHeader>
                <SheetTitle>{t("administrativeDocumentCreerTitle")}</SheetTitle>
              </CustomSheetHeader>
              <CustomSheetBody>
                <AF.Col className="gap-4">
                  <Label className="text-lg">{t("printOptions")}</Label>
                  {selectedPermanentEnsemble != undefined && (
                    <AF.Col className="gap-2">
                      <FormLabel>{t("documentType")}</FormLabel>
                      <FormField
                        control={form.control}
                        name="documentType"
                        render={({ field }) => (
                          <HeadlessFormItem>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                                className="flex flex-col"
                              >
                                <FormItem className="flex items-center space-x-3 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="document1" />
                                  </FormControl>
                                  <FormLabel className="font-normal">
                                    {
                                      selectedPermanentEnsemble.ensemblesTitreDocument1
                                    }
                                  </FormLabel>
                                </FormItem>
                                <FormItem className="flex items-center space-x-3 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="document2" />
                                  </FormControl>
                                  <FormLabel className="font-normal">
                                    {
                                      selectedPermanentEnsemble.ensemblesTitreDocument2
                                    }
                                  </FormLabel>
                                </FormItem>
                              </RadioGroup>
                            </FormControl>
                          </HeadlessFormItem>
                        )}
                      />
                      <AF.Checkbox
                        name="hasBeenExamined"
                        label={t("oeuvreExaminee")}
                      />
                    </AF.Col>
                  )}
                  <AF.Text name="signingInfo" label={t("signloca")} />
                  <Separator />
                  <div className="flex flex-col gap-4">
                    <span className="font-semibold font-[Raleway] text-base">
                      {t("artworksIncluded")}
                    </span>

                    <SelectedRecord
                      image={image}
                      title={artwork.oeuvresDescriptionTitre}
                    />
                  </div>
                </AF.Col>
              </CustomSheetBody>
              <CustomDialogFooter>
                <SheetClose asChild>
                  <CancelButton className="flex-1" />
                </SheetClose>
                <Button
                  className="flex-1"
                  type="submit"
                  variant="default"
                  disabled={!form.formState.isValid}
                >
                  {pdfGenerator.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  {t("administrativeDocumentsPrevisualiserBouton")}
                </Button>
              </CustomDialogFooter>
            </form>
          </Form>
        </CustomSheetContent>
      </Sheet>
    </>
  );
};
